import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { agoText } from "../chat/api";
import { EXPIRES_CHOICES, daysFromNow } from "../lib/mcpTokens";
import type { McpToken } from "../lib/mcpTokens";
import { tx } from "../i18n";
import { Button } from "./Button";
import { Alert, EmptyState } from "./Card";
import { Field, Input, Select } from "./Field";
import "./Runner.css";
import "./McpTokens.css";
import { ScopePicker, ScopeText } from "./ProjectScope";
import type { ProjectName } from "./ProjectScope";

/* ══ 설정 › MCP 토큰 부품 — 시안 docs/screens/pr-port.md §23 ══
   설정 › 러너의 「토큰 직접 발급」과 같은 모양이다(표 · 위에 펼치는 폼 · 한 번만 보이는 카드 — .rtable · .rnew ·
   .rtoken 을 같이 쓴다). 한 줄 단추는 「관리」 하나 — 이름 · 만료 · 범위 · 폐기는 표 아래 펼치는 카드에 있다. */

const day = (iso: string) => new Date(iso).toLocaleDateString("sv-SE");

/** 만료 칸 — 무제한 · 날짜 · 지남(모양 ⚠ + 글자). */
export function ExpiryText({ t }: { t: Pick<McpToken, "expiresAt" | "expired"> }) {
  if (!t.expiresAt) return <span>{tx("mcpTokens.expires.never")}</span>;
  if (t.expired)
    return (
      <span className="b-badge b-badge-danger">
        <span aria-hidden="true">⚠</span> {tx("mcpTokens.expires.past", { date: day(t.expiresAt) })}
      </span>
    );
  return <span className="tnum">{day(t.expiresAt)}</span>;
}

export function McpTokenTable({
  rows,
  projects,
  picked,
  now = Date.now(),
  onPick,
}: {
  rows: McpToken[];
  projects: ProjectName[];
  picked?: string | null;
  now?: number;
  onPick?: (name: string) => void;
}) {
  if (rows.length === 0) {
    return <EmptyState icon="inbox" title={tx("mcpTokens.empty.title")} desc={tx("mcpTokens.empty.desc")} />;
  }
  return (
    <div className="b-table-wrap">
      <table className="b-table mtable" aria-label={tx("mcpTokens.title")}>
        <thead>
          <tr>
            <th>{tx("mcpTokens.col.name")}</th>
            <th>{tx("mcpTokens.col.created")}</th>
            <th>{tx("mcpTokens.col.used")}</th>
            <th>{tx("mcpTokens.col.expires")}</th>
            <th>{tx("mcpTokens.col.scope")}</th>
            <th>{tx("mcpTokens.col.action")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.name} className={picked === t.name ? "is-selected" : undefined}>
              <td className="is-primary">{t.name}</td>
              <td>{tx("mcpTokens.createdBy", { by: t.createdBy, date: day(t.createdAt) })}</td>
              <td>{t.lastUsedAt ? agoText(t.lastUsedAt, now) : tx("mcpTokens.neverUsed")}</td>
              <td>
                <ExpiryText t={t} />
              </td>
              <td>
                <ScopeText projects={t.projects} names={projects} />
              </td>
              <td>
                <Button size="sm" variant="ghost" aria-expanded={picked === t.name} onClick={() => onPick?.(t.name)}>
                  {tx("mcpTokens.manage")}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const expiresLabel = (d: number | null) => (d === null ? tx("mcpTokens.expires.never") : tx("mcpTokens.expires.days", { n: d }));

export function NewMcpTokenForm({
  projects,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  projects: ProjectName[];
  busy?: boolean;
  error?: string | null;
  onSubmit?: (b: { name: string; expiresDays: number | null; projects: string[] | null }) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState("");
  const [days, setDays] = useState<number | null>(null);
  const [scope, setScope] = useState<string[] | null>(null);
  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit?.({ name: name.trim(), expiresDays: days, projects: scope });
  }
  return (
    <section className="b-card rnew">
      <h3 className="b-card-title">{tx("mcpTokens.new.title")}</h3>
      {error ? <Alert tone="error">{error}</Alert> : null}
      <form className="rnew__form" onSubmit={submit}>
        <Field label={tx("mcpTokens.form.name")} hint={tx("mcpTokens.form.nameHint")}>
          {(id) => <Input id={id} autoComplete="off" spellCheck={false} placeholder="claude-code" value={name} onChange={(e) => setName(e.target.value)} required />}
        </Field>
        <ScopePicker name="mscope-new" value={scope} projects={projects} onChange={setScope} />
        <Field label={tx("mcpTokens.form.expires")}>
          {(id) => (
            <Select id={id} value={days === null ? "" : String(days)} onChange={(e) => setDays(e.target.value ? Number(e.target.value) : null)}>
              {EXPIRES_CHOICES.map((d) => (
                <option key={String(d)} value={d === null ? "" : String(d)}>
                  {expiresLabel(d)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="rnew__actions">
          <Button type="button" onClick={onCancel}>
            {tx("common.action.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {tx("mcpTokens.new.submit")}
          </Button>
        </div>
      </form>
    </section>
  );
}

/** 「관리」로 펼치는 카드 — 이름 · 만료 다시 정하기 · 범위 · 폐기(두 번 누름). 만료 고르기의 빈 값은
 *  「그대로 둔다」다 — 무제한과 다르다(세 상태를 접지 않는다). */
export function McpTokenEdit({
  t,
  projects,
  busy,
  error,
  armed: armedInit = false,
  onSave,
  onRevoke,
  onClose,
}: {
  t: McpToken;
  projects: ProjectName[];
  busy?: boolean;
  error?: string | null;
  armed?: boolean;
  onSave?: (b: { name?: string; expiresAt?: string | null; projects?: string[] | null }) => void;
  onRevoke?: () => void;
  onClose?: () => void;
}) {
  const [name, setName] = useState(t.name);
  const [exp, setExp] = useState<"keep" | "never" | number>("keep");
  const [scope, setScope] = useState<string[] | null>(t.projects);
  const [armed, setArmed] = useState(armedInit);
  useEffect(() => {
    setName(t.name);
    setExp("keep");
    setScope(t.projects);
    setArmed(armedInit);
  }, [t, armedInit]);
  function submit(e: FormEvent) {
    e.preventDefault();
    const b: { name?: string; expiresAt?: string | null; projects?: string[] | null } = {};
    if (name.trim() !== t.name) b.name = name.trim();
    if (exp === "never") b.expiresAt = null;
    else if (typeof exp === "number") b.expiresAt = daysFromNow(exp);
    if (JSON.stringify(scope) !== JSON.stringify(t.projects)) b.projects = scope;
    onSave?.(b);
  }
  return (
    <section className="b-card rnew medit">
      <h3 className="b-card-title">{tx("mcpTokens.edit.title", { name: t.name })}</h3>
      {error ? <Alert tone="error">{error}</Alert> : null}
      <form className="rnew__form" onSubmit={submit}>
        <Field label={tx("mcpTokens.form.name")} hint={tx("mcpTokens.edit.nameHint")}>
          {(id) => <Input id={id} autoComplete="off" spellCheck={false} value={name} onChange={(e) => setName(e.target.value)} required />}
        </Field>
        <Field label={tx("mcpTokens.form.expires")} hint={tx("mcpTokens.edit.expiresNow", { now: t.expiresAt ? day(t.expiresAt) : tx("mcpTokens.expires.never") })}>
          {(id) => (
            <Select
              id={id}
              value={String(exp)}
              onChange={(e) => {
                const v = e.target.value;
                setExp(v === "keep" || v === "never" ? v : Number(v));
              }}
            >
              <option value="keep">{tx("mcpTokens.edit.keep")}</option>
              <option value="never">{tx("mcpTokens.expires.never")}</option>
              {EXPIRES_CHOICES.filter((d): d is number => d !== null).map((d) => (
                <option key={d} value={String(d)}>
                  {tx("mcpTokens.edit.fromToday", { n: d })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <ScopePicker name={`mscope-${t.name}`} value={scope} projects={projects} onChange={setScope} />
        <div className="rnew__actions">
          <Button
            type="button"
            variant={armed ? "danger" : "ghost"}
            disabled={busy}
            onClick={() => (armed ? onRevoke?.() : setArmed(true))}
            onBlur={() => setArmed(false)}
          >
            {armed ? tx("mcpTokens.revokeConfirm") : tx("runners.revoke")}
          </Button>
          <span className="medit__gap" />
          <Button type="button" onClick={onClose} disabled={busy}>
            {tx("common.action.close")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {tx("common.action.save")}
          </Button>
        </div>
        {armed ? <p className="caption">{tx("mcpTokens.revokeNote")}</p> : null}
      </form>
    </section>
  );
}

/** 발급 직후 한 번만 서는 카드 — 원문 + 복사 + 하네스에 넣는 한 줄. 닫으면 화면 어디에도 없다. */
export function McpTokenCard({ name, token, mcpUrl, onClose }: { name: string; token: string; mcpUrl: string; onClose?: () => void }) {
  const [copied, setCopied] = useState<"ok" | "fail" | null>(null);
  useEffect(() => setCopied(null), [token]);
  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied("ok");
    } catch {
      setCopied("fail");
    }
  }
  const cmd = `claude mcp add --transport http b3x-in ${mcpUrl} --header "Authorization: Bearer ${token}"`;
  return (
    <section className="b-card rtoken" aria-live="polite">
      <h3 className="b-card-title">{tx("mcpTokens.token.title", { name })}</h3>
      <p className="muted">{tx("runners.token.lead")}</p>
      <div className="rtoken__row">
        <code className="rtoken__value">{token}</code>
        <Button size="md" onClick={() => copy(token)}>
          {tx("runners.token.copy")}
        </Button>
      </div>
      {copied === "ok" ? <Alert tone="success">{tx("runners.token.copied")}</Alert> : null}
      {copied === "fail" ? <Alert tone="warning">{tx("runners.token.copyFailed")}</Alert> : null}
      <p className="muted">{tx("mcpTokens.token.where")}</p>
      <div className="rtoken__row">
        <pre className="rtoken__cmds mtoken__cmd">{cmd}</pre>
        <Button size="md" onClick={() => copy(cmd)}>
          {tx("common.action.copy")}
        </Button>
      </div>
      <p className="caption">{tx("mcpTokens.token.linkNote")}</p>
      <div className="rnew__actions">
        <Button variant="primary" onClick={onClose}>
          {tx("runners.token.done")}
        </Button>
      </div>
    </section>
  );
}
